import { Vec3 } from '../../engine/math/vec3';
import { clamp, easeInCubic, easeOutCubic } from '../../engine/math/easing';
import { sphere, box, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, scale as scaleMesh } from '../../engine/geo/mesh';
import type { Color } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Player } from '../player/Player';
import { Beast } from './Beast';
import { MARSH_WATER_Y } from '../world/Terrain';

const SEGMENTS = 9;
const COLOR: Color = [0.62, 0.72, 0.22];

/**
 * Bog Eel (§5): a chain of 9 spheres that lives in shallow water. Lurks with only the head showing,
 * moves in S-curves, lunges 6 m along the surface after a 0.6 s telegraph, then lies beached for 1.2 s.
 * Cannot leave water.
 */
export class BogEel extends Beast {
  private readonly segs: Vec3[] = [];
  private readonly radii: number[] = [];
  private readonly headTarget = new Vec3();
  private heading = 0;
  private wander = 0;
  private lungeFrom = new Vec3();
  private lungeTo = new Vec3();
  private bit = false;
  private readonly home: Vec3;
  private headLift = 0;

  constructor(ctx: Ctx, pos: Vec3) {
    super(ctx, 'eel', pos, 3, COLOR, 40);
    this.home = pos.clone();
    for (let i = 0; i < SEGMENTS; i++) {
      const r = 0.55 - i * 0.04;
      this.radii.push(r);
      const p = pos.clone(); p.z += i * r * 1.6; p.y = MARSH_WATER_Y - 0.15;
      this.segs.push(p);
      const shade: Color = [COLOR[0] * (1 - i * 0.03), COLOR[1] * (1 - i * 0.03), COLOR[2]];
      let mesh = sphere(r, 9, 6, shade);
      if (i === 0) {
        const eyeL = setEmissive(translate(sphere(0.09, 6, 4, [1, 0.9, 0.3]), 0.25, 0.25, 0.35), [1, 0.8, 0.2], 1.2);
        const eyeR = setEmissive(translate(sphere(0.09, 6, 4, [1, 0.9, 0.3]), -0.25, 0.25, 0.35), [1, 0.8, 0.2], 1.2);
        const jaw = translate(scaleMesh(sphere(0.42, 8, 5, [0.5, 0.58, 0.18]), 1, 0.5, 1.2), 0, -0.2, 0.2);
        mesh = combine(mesh, eyeL, eyeR, jaw);
      }
      const part = this.addPart('eel.seg' + i, mesh, this.root, new Vec3(), r);
      part.t.position.copy(p).sub(pos);
      // crack overlay: dark stripes
      const crack = combine(
        setEmissive(translate(box(0.06, r * 1.9, 0.06, [0.05, 0.05, 0.03]), r * 0.5, 0, r * 0.7), [0.02, 0.02, 0.01], 0.2),
        setEmissive(translate(box(0.06, r * 1.7, 0.06, [0.05, 0.05, 0.03]), -r * 0.6, 0, -r * 0.4), [0.02, 0.02, 0.01], 0.2),
      );
      this.addCrack('eel.crack' + i, crack, part.t);
    }
    this.headTarget.copy(this.segs[0]);
  }

  /** Water: terrain below the surface. */
  private inWater(x: number, z: number, margin = 0.15): boolean {
    return this.ctx.statics.terrainHeight(x, z) < MARSH_WATER_Y - margin;
  }

  protected onKnockback(dir: Vec3): void {
    const h = this.segs[0];
    const nx = h.x + dir.x * 0.8, nz = h.z + dir.z * 0.8;
    if (this.inWater(nx, nz)) { h.x = nx; h.z = nz; }
    if (this.state === 'recover') this.stateT = Math.max(this.stateT, 0.4); // stays vulnerable a bit less
  }

  update(dt: number, player: Player): void {
    this.tick(dt);
    if (!this.alive) return;
    const head = this.segs[0];
    const engaged = this.updateAggro(player, dt);
    const sp = this.speedMul;
    const toPlayer = _v.copy(player.position).sub(head); toPlayer.y = 0;
    const dist = toPlayer.length();

    switch (this.state) {
      case 'idle':
      case 'alert': {
        // S-curve swim: heading wobbles; steer toward player or wander around home
        this.wander += dt;
        const steer = engaged ? Math.atan2(toPlayer.x, toPlayer.z) : Math.atan2(this.home.x - head.x, this.home.z - head.z);
        const wantWander = !engaged && head.distanceTo(this.home) < 8;
        let target = wantWander ? this.heading + Math.sin(this.wander * 0.7) * 0.9 : steer + Math.sin(this.wander * 2.5) * 0.6;
        let diff = target - this.heading;
        diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        this.heading += clamp(diff, -1.8 * dt, 1.8 * dt);
        const speed = (engaged ? 3.2 : 1.1) * sp;
        const nx = head.x + Math.sin(this.heading) * speed * dt, nz = head.z + Math.cos(this.heading) * speed * dt;
        if (this.inWater(nx, nz)) { head.x = nx; head.z = nz; } else { this.heading += 2.5 * dt; }
        this.headLift = engaged ? 0.25 : 0.05;
        this.state = engaged ? 'alert' : 'idle';
        if (engaged && dist < 6.5 && this.canSee(player) && this.inWater(head.x, head.z, 0)) {
          this.setState('telegraph');
          this.ctx.sfx.telegraph('eel', head, 0.6);
        }
        break;
      }
      case 'telegraph': {
        // head rises and squashes back
        const k = this.stateT / 0.6;
        this.headLift = 0.3 + easeInCubic(clamp(k, 0, 1)) * 0.9;
        head.x -= Math.sin(this.heading) * 0.6 * dt; head.z -= Math.cos(this.heading) * 0.6 * dt;
        this.heading = Math.atan2(toPlayer.x, toPlayer.z);
        if (this.stateT >= 0.6) {
          this.setState('attack');
          this.lungeFrom.copy(head);
          const dir = _v2.set(Math.sin(this.heading), 0, Math.cos(this.heading));
          this.lungeTo.copy(head).addScaled(dir, 6);
          this.bit = false;
        }
        break;
      }
      case 'attack': {
        const k = clamp(this.stateT / 0.32, 0, 1);
        const e = easeOutCubic(k);
        head.x = this.lungeFrom.x + (this.lungeTo.x - this.lungeFrom.x) * e;
        head.z = this.lungeFrom.z + (this.lungeTo.z - this.lungeFrom.z) * e;
        this.headLift = 0.6 * (1 - k) + 0.1;
        if (!this.bit && k > 0.2) {
          const c = _v2.copy(head); c.y = player.position.y + 0.5;
          if (this.tryHitPlayer(player, c, 1.0, 2)) this.bit = true;
        }
        if (k >= 1) { this.setState('recover'); this.ctx.fx.shake(0.08, 0.2); }
        break;
      }
      case 'recover': {
        // beached: head lies still on whatever it landed on
        this.headLift = 0.02;
        if (this.stateT >= 1.2) {
          this.setState('alert');
          // slide back into water if beached on land
          if (!this.inWater(head.x, head.z)) {
            const back = _v2.copy(this.lungeFrom).sub(head).normalize();
            this.heading = Math.atan2(back.x, back.z);
          }
        }
        break;
      }
      default: break;
    }

    // chain follow: each segment trails the previous at its rest distance
    for (let i = 1; i < SEGMENTS; i++) {
      const prev = this.segs[i - 1], cur = this.segs[i];
      const rest = (this.radii[i - 1] + this.radii[i]) * 0.85;
      const d = _v2.copy(cur).sub(prev);
      d.y = 0;
      const l = d.length();
      if (l > rest) { d.scale(rest / l); cur.x = prev.x + d.x; cur.z = prev.z + d.z; }
      else if (l < rest * 0.6 && l > 1e-4) { d.scale(rest * 0.6 / l); cur.x = prev.x + d.x; cur.z = prev.z + d.z; }
    }
    // pose: head y from water level + lift; body undulates
    const t = this.ctx.time;
    const groundHead = this.ctx.statics.terrainHeight(head.x, head.z);
    const surface = Math.max(MARSH_WATER_Y, groundHead + 0.35);
    head.y = surface + this.headLift - 0.15;
    for (let i = 1; i < SEGMENTS; i++) {
      const s = this.segs[i];
      const g = this.ctx.statics.terrainHeight(s.x, s.z);
      const base = Math.max(MARSH_WATER_Y - 0.25, g + this.radii[i] * 0.6);
      s.y = base + Math.sin(t * 3 + i * 0.9) * 0.08 * (this.state === 'alert' ? 1.6 : 1) + (i < 3 ? this.headLift * (0.6 - i * 0.2) : 0);
    }
    // write to transforms (root stays at the head so culling/aggro use the head)
    this.root.position.copy(head);
    for (let i = 0; i < SEGMENTS; i++) {
      const p = this.parts[i];
      p.t.position.copy(this.segs[i]).sub(head);
      if (i === 0) {
        p.t.rotation.setEuler(this.heading, this.state === 'telegraph' ? -0.6 : this.state === 'attack' ? 0.4 : 0.1, 0);
        const sq = this.state === 'telegraph' ? 1 - 0.25 * clamp(this.stateT / 0.6, 0, 1) : 1;
        p.t.scale.set(1 / Math.sqrt(sq), sq * (this.state === 'telegraph' ? 1.1 : 1), sq);
      } else {
        const next = this.segs[i - 1];
        p.t.rotation.setYaw(Math.atan2(next.x - this.segs[i].x, next.z - this.segs[i].z));
      }
    }
  }
}
const _v = new Vec3(), _v2 = new Vec3();
